/**
 * Generato automaticamente.
 * framework-cfg-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetTipiPraticaParams,
  GetTipiPraticaTipoProcedimentoAssociabiliParams,
  TipoPraticaDTO,
  TipoPraticaRequest,
  TipoProcedimentoDTO,
  WsRispostaPaginataListTipoPraticaDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce il tipo pratica con chiave id
 * @summary Restituisce tipo pratica
 */
export const getTipiPraticaId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoPraticaDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-pratica/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTipiPraticaIdQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/tipi-pratica/${id}`] as const;

export const getGetTipiPraticaIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiPraticaId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiPraticaId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiPraticaId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTipiPraticaIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiPraticaId>>
  > = ({ signal }) => getTipiPraticaId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTipiPraticaIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiPraticaId>>
>;
export type GetTipiPraticaIdQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce tipo pratica
 */
export const useGetTipiPraticaId = <
  TData = Awaited<ReturnType<typeof getTipiPraticaId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiPraticaId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiPraticaIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna tipo pratica
 * @summary Aggiorna tipo pratica
 */
export const putTipiPraticaId = (
  id: string,
  tipoPraticaRequest: TipoPraticaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TipoPraticaDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-pratica/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: tipoPraticaRequest,
    },
    options,
  );
};

export const getPutTipiPraticaIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiPraticaId>>,
    TError,
    { id: string; data: TipoPraticaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putTipiPraticaId>>,
  TError,
  { id: string; data: TipoPraticaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putTipiPraticaId>>,
    { id: string; data: TipoPraticaRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putTipiPraticaId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutTipiPraticaIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putTipiPraticaId>>
>;
export type PutTipiPraticaIdMutationBody = TipoPraticaRequest;
export type PutTipiPraticaIdMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna tipo pratica
 */
export const usePutTipiPraticaId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiPraticaId>>,
    TError,
    { id: string; data: TipoPraticaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutTipiPraticaIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cancellazione tipo pratica
 * @summary Cancellazione tipo pratica
 */
export const deleteTipiPraticaId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-procedimenti/tipi-pratica/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteTipiPraticaIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipiPraticaId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteTipiPraticaId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteTipiPraticaId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteTipiPraticaId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTipiPraticaIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteTipiPraticaId>>
>;

export type DeleteTipiPraticaIdMutationError = ErrorType<unknown>;

/**
 * @summary Cancellazione tipo pratica
 */
export const useDeleteTipiPraticaId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipiPraticaId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteTipiPraticaIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce i tipi pratica che soddisfano i criteri di ricerca
 * @summary Ricerca tipi pratica paginata
 */
export const getTipiPratica = (
  params?: GetTipiPraticaParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListTipoPraticaDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-pratica`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetTipiPraticaQueryKey = (params?: GetTipiPraticaParams) =>
  [
    `/framework-cfg-procedimenti/tipi-pratica`,
    ...(params ? [params] : []),
  ] as const;

export const getGetTipiPraticaQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiPratica>>,
  TError = ErrorType<unknown>,
>(
  params?: GetTipiPraticaParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiPratica>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiPratica>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTipiPraticaQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTipiPratica>>> = ({
    signal,
  }) => getTipiPratica(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTipiPraticaQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiPratica>>
>;
export type GetTipiPraticaQueryError = ErrorType<unknown>;

/**
 * @summary Ricerca tipi pratica paginata
 */
export const useGetTipiPratica = <
  TData = Awaited<ReturnType<typeof getTipiPratica>>,
  TError = ErrorType<unknown>,
>(
  params?: GetTipiPraticaParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiPratica>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiPraticaQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiunge un nuovo tipo di pratica
 * @summary Aggiunta nuovo tipo pratica
 */
export const postTipiPratica = (
  tipoPraticaRequest: TipoPraticaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TipoPraticaDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-pratica`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: tipoPraticaRequest,
    },
    options,
  );
};

export const getPostTipiPraticaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiPratica>>,
    TError,
    { data: TipoPraticaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postTipiPratica>>,
  TError,
  { data: TipoPraticaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postTipiPratica>>,
    { data: TipoPraticaRequest }
  > = props => {
    const { data } = props ?? {};

    return postTipiPratica(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTipiPraticaMutationResult = NonNullable<
  Awaited<ReturnType<typeof postTipiPratica>>
>;
export type PostTipiPraticaMutationBody = TipoPraticaRequest;
export type PostTipiPraticaMutationError = ErrorType<unknown>;

/**
 * @summary Aggiunta nuovo tipo pratica
 */
export const usePostTipiPratica = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiPratica>>,
    TError,
    { data: TipoPraticaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostTipiPraticaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Recupera tipi procedimento non associati a tipi pratica
 * @summary Ricerca tipi procedimento non associati al tipo pratica
 */
export const getTipiPraticaTipoProcedimentoAssociabili = (
  params?: GetTipiPraticaTipoProcedimentoAssociabiliParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoProcedimentoDTO[]>(
    {
      url: `/framework-cfg-procedimenti/tipi-pratica/tipo-procedimento-associabili`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetTipiPraticaTipoProcedimentoAssociabiliQueryKey = (
  params?: GetTipiPraticaTipoProcedimentoAssociabiliParams,
) =>
  [
    `/framework-cfg-procedimenti/tipi-pratica/tipo-procedimento-associabili`,
    ...(params ? [params] : []),
  ] as const;

export const getGetTipiPraticaTipoProcedimentoAssociabiliQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiPraticaTipoProcedimentoAssociabili>>,
  TError = ErrorType<unknown>,
>(
  params?: GetTipiPraticaTipoProcedimentoAssociabiliParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiPraticaTipoProcedimentoAssociabili>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiPraticaTipoProcedimentoAssociabili>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTipiPraticaTipoProcedimentoAssociabiliQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiPraticaTipoProcedimentoAssociabili>>
  > = ({ signal }) =>
    getTipiPraticaTipoProcedimentoAssociabili(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTipiPraticaTipoProcedimentoAssociabiliQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiPraticaTipoProcedimentoAssociabili>>
>;
export type GetTipiPraticaTipoProcedimentoAssociabiliQueryError =
  ErrorType<unknown>;

/**
 * @summary Ricerca tipi procedimento non associati al tipo pratica
 */
export const useGetTipiPraticaTipoProcedimentoAssociabili = <
  TData = Awaited<ReturnType<typeof getTipiPraticaTipoProcedimentoAssociabili>>,
  TError = ErrorType<unknown>,
>(
  params?: GetTipiPraticaTipoProcedimentoAssociabiliParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiPraticaTipoProcedimentoAssociabili>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiPraticaTipoProcedimentoAssociabiliQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
